// schema
import { useMemo } from "react";

import { CohortENPSRow } from "./CohortENPSDetailsTable";

// store
import useStore from "../../../../store";

// hooks
import { useCohortENPSData, useCohorts, useNPSSummary } from "../../../../../../api/hooks";

// Config
import Config from "../../../../Config";

/**
 * Hook to make an api call based on chnages the engagement date range filter state
 * and return rows formatted for the CohortENPSDetailsTable component
 */
const useCohortENPSRows = (): {
	rows: CohortENPSRow[];
	isLoading: boolean;
	getCSVContent: () => string;
	selectCSVDownloadName: () => string;
} => {
	const [filter] = useStore((state) => [state.filter]);
	const { data: cohortData, isLoading: isCohortsLoading } = useCohorts();
	// by cohort eNPS data
	const { data, isLoading: isCohortENPSLoading } = useCohortENPSData({
		startDate: filter.startDate,
		endDate: filter.endDate,
	});
	// All employees data
	const { data: allEmployeesData, isLoading: isAllEmployeesLoading } = useNPSSummary({
		startDate: filter.startDate,
		endDate: filter.endDate,
	});

	const isLoading = isCohortsLoading || isCohortENPSLoading || isAllEmployeesLoading;

	const rows = useMemo(() => {
		const percentage = (value: number, count: number) => {
			return (value / count) * 100;
		};
		const cohortsData =
			// cohort rows
			data?.data
				.filter((cohortData) => {
					return cohortData.promoters > 0 || cohortData.neutrals > 0 || cohortData.detractors > 0;
				})
				.map((cohortENPSData) => {
					const {
						cohort,
						eNPS_score,
						promoters,
						promotersAverage,
						neutrals,
						neutralsAverage,
						detractors,
						detractorsAverage,
						count,
					} = cohortENPSData;
					const cohortName = cohortData?.cohorts.find((c) => c._id === cohort)?.name || "";
					return {
						cohortName,
						eNPSData: {
							eNPSScore: Math.round(eNPS_score),
							percentages: [
								percentage(detractors, count),
								percentage(neutrals, count),
								percentage(promoters, count),
							],
							averages: [detractorsAverage, neutralsAverage, promotersAverage],
							counts: [detractors, neutrals, promoters],
						},
					};
				}) || [];
		// all employees row
		const allEmployeesRow: CohortENPSRow[] = [];
		const allEmployees = allEmployeesData?.data[0];
		if (allEmployees) {
			const {
				eNPS_score,
				promoters,
				promotersAverage,
				neutrals,
				neutralsAverage,
				detractors,
				detractorsAverage,
				count,
			} = allEmployees;
			const allEmployeesENPS = allEmployees ? Math.round(eNPS_score) : null;
			if (allEmployeesENPS) {
				allEmployeesRow.push({
					cohortName: "All Employees",
					eNPSData: {
						eNPSScore: allEmployeesENPS,
						percentages: [
							percentage(detractors, count),
							percentage(neutrals, count),
							percentage(promoters, count),
						],
						averages: [detractorsAverage, neutralsAverage, promotersAverage],
						counts: [detractors, neutrals, promoters],
					},
				});
			}
		}
		// combine with all employees in front
		return [...allEmployeesRow, ...cohortsData];
	}, [data, allEmployeesData]);

	const formatDecimalValue = (value: number | null) => {
		if (value === null) return "";
		return parseFloat(value.toFixed(1));
	};

	const getCSVContent = (): string => {
		if (!rows.length) return "";
		const csvColumns = [
			"Cohort",
			"eNPS Score",
			"Detractors %",
			"Detractors #",
			"Detractors Average",
			"Passives %",
			"Passives #",
			"Passives Average",
			"Promoters %",
			"Promoters #",
			"Promoters Average",
		];
		// [...data.map((d) => `"${d.data[key] || ""}"`)].join(",")
		const csvRows = rows.map((row) => [
			// Cohort name (escape commas and double quotes for csv)
			`"${row.cohortName.replace(/"/g, '""')}"`,
			// eNPS Score
			row.eNPSData.eNPSScore,
			// Detractors
			formatDecimalValue(row.eNPSData.percentages[0]),
			row.eNPSData.counts?.[0],
			formatDecimalValue(row.eNPSData.averages[0]),
			// Passives
			formatDecimalValue(row.eNPSData.percentages[1]),
			row.eNPSData.counts?.[1],
			formatDecimalValue(row.eNPSData.averages[1]),
			// Promoters
			formatDecimalValue(row.eNPSData.percentages[2]),
			row.eNPSData.counts?.[2],
			formatDecimalValue(row.eNPSData.averages[2]),
		]);
		return [csvColumns, ...csvRows].join("\n");
	};

	const selectCSVDownloadName = () => {
		const date = new Date().toDateString();
		const timePeriod = Config.FILTER_OPTIONS.find((option) => option.value === filter.value)?.label;
		return `PerformYard-Engagement-eNPS-${timePeriod}-${date}.csv`;
	};

	return { rows, isLoading, getCSVContent, selectCSVDownloadName };
};

export default useCohortENPSRows;
